import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft } from 'lucide-react';
import SEOHead from '../components/common/SEOHead';

export default function PrivacyPolicy() {
  const { t } = useTranslation('legal');
  return (
    <div className="pub-page min-h-screen py-12 px-4">
      <SEOHead
        title="Politique de confidentialité"
        description="Découvrez comment Splinity protège vos données personnelles et financières. RGPD, chiffrement AES-256, suppression sur demande."
        path="/politique-confidentialite"
      />
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center text-primary-600 hover:text-primary-700 mb-8">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('backToHome')}
        </Link>

        <div className="pub-card p-8">
          <h1 className="pub-title text-3xl font-bold mb-8">{t('privacyPolicy.title')}</h1>

          <div className="pub-prose max-w-none">
            <p className="lead">{t('privacyPolicy.intro')}</p>

            <h2>{t('privacyPolicy.controller.title')}</h2>
            <p>{t('privacyPolicy.controller.content')}</p>

            <h2>{t('privacyPolicy.dataCollected.title')}</h2>
            <p>{t('privacyPolicy.dataCollected.intro')}</p>
            <ul>
              <li><strong>{t('privacyPolicy.dataCollected.identification')}</strong> : {t('privacyPolicy.dataCollected.identificationDesc')}</li>
              <li><strong>{t('privacyPolicy.dataCollected.financial')}</strong> : {t('privacyPolicy.dataCollected.financialDesc')}</li>
              <li><strong>{t('privacyPolicy.dataCollected.technical')}</strong> : {t('privacyPolicy.dataCollected.technicalDesc')}</li>
              <li><strong>{t('privacyPolicy.dataCollected.payment')}</strong> : {t('privacyPolicy.dataCollected.paymentDesc')}</li>
            </ul>

            <h2>{t('privacyPolicy.purposes.title')}</h2>
            <p>{t('privacyPolicy.purposes.intro')}</p>
            <ul>
              <li>{t('privacyPolicy.purposes.service')}</li>
              <li>{t('privacyPolicy.purposes.calculation')}</li>
              <li>{t('privacyPolicy.purposes.security')}</li>
              <li>{t('privacyPolicy.purposes.notifications')}</li>
              <li>{t('privacyPolicy.purposes.billing')}</li>
            </ul>

            <h2>{t('privacyPolicy.legalBasis.title')}</h2>
            <p>{t('privacyPolicy.legalBasis.content')}</p>

            <h2>{t('privacyPolicy.recipients.title')}</h2>
            <p>{t('privacyPolicy.recipients.content')}</p>

            <h2>{t('privacyPolicy.retention.title')}</h2>
            <ul>
              <li><strong>{t('privacyPolicy.retention.account')}</strong> : {t('privacyPolicy.retention.accountDesc')}</li>
              <li><strong>{t('privacyPolicy.retention.financial')}</strong> : {t('privacyPolicy.retention.financialDesc')}</li>
              <li><strong>{t('privacyPolicy.retention.logs')}</strong> : {t('privacyPolicy.retention.logsDesc')}</li>
              <li><strong>{t('privacyPolicy.retention.invoices')}</strong> : {t('privacyPolicy.retention.invoicesDesc')}</li>
              <li><strong>{t('privacyPolicy.retention.prelaunch')}</strong> : {t('privacyPolicy.retention.prelaunchDesc')}</li>
            </ul>

            <h2>{t('privacyPolicy.rights.title')}</h2>
            <p>{t('privacyPolicy.rights.intro')}</p>
            <ul>
              <li><strong>{t('privacyPolicy.rights.access')}</strong> : {t('privacyPolicy.rights.accessDesc')}</li>
              <li><strong>{t('privacyPolicy.rights.rectification')}</strong> : {t('privacyPolicy.rights.rectificationDesc')}</li>
              <li><strong>{t('privacyPolicy.rights.erasure')}</strong> : {t('privacyPolicy.rights.erasureDesc')}</li>
              <li><strong>{t('privacyPolicy.rights.portability')}</strong> : {t('privacyPolicy.rights.portabilityDesc')}</li>
              <li><strong>{t('privacyPolicy.rights.opposition')}</strong> : {t('privacyPolicy.rights.oppositionDesc')}</li>
            </ul>
            <p>{t('privacyPolicy.rights.exerciseRights')}</p>

            <h2>{t('privacyPolicy.securityMeasures.title')}</h2>
            <p>{t('privacyPolicy.securityMeasures.intro')}</p>
            <ul>
              <li>{t('privacyPolicy.securityMeasures.encryption')}</li>
              <li>{t('privacyPolicy.securityMeasures.hashing')}</li>
              <li>{t('privacyPolicy.securityMeasures.firewall')}</li>
              <li>{t('privacyPolicy.securityMeasures.backups')}</li>
              <li>{t('privacyPolicy.securityMeasures.limitedAccess')}</li>
              <li>{t('privacyPolicy.securityMeasures.pci')}</li>
            </ul>

            <h2>{t('privacyPolicy.cookiesSection.title')}</h2>
            <p>{t('privacyPolicy.cookiesSection.intro')}</p>
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left">{t('privacyPolicy.cookiesSection.cookie')}</th>
                  <th className="text-left">{t('privacyPolicy.cookiesSection.purpose')}</th>
                  <th className="text-left">{t('privacyPolicy.cookiesSection.duration')}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>session_id</td>
                  <td>{t('privacyPolicy.cookiesSection.session')}</td>
                  <td>{t('privacyPolicy.cookiesSection.sessionDuration')}</td>
                </tr>
                <tr>
                  <td>remember_me</td>
                  <td>{t('privacyPolicy.cookiesSection.rememberMe')}</td>
                  <td>{t('privacyPolicy.cookiesSection.rememberMeDuration')}</td>
                </tr>
                <tr>
                  <td>cookie_consent</td>
                  <td>{t('privacyPolicy.cookiesSection.consent')}</td>
                  <td>{t('privacyPolicy.cookiesSection.consentDuration')}</td>
                </tr>
              </tbody>
            </table>

            <h2>{t('privacyPolicy.complaint.title')}</h2>
            <p>
              {t('privacyPolicy.complaint.content')}{' '}
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary-600">www.cnil.fr</a>
            </p>

            <h2>{t('privacyPolicy.modifications.title')}</h2>
            <p>{t('privacyPolicy.modifications.content')}</p>

            <p className="pub-subtle text-sm mt-8">
              {t('lastUpdated')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
